// Shared helpers for rendering multi-day activities across month/week/list views.
// All date math is done in the viewer's LOCAL timezone (the browser's zone),
// so an event stored as UTC `2026-11-15T03:00:00Z` is correctly placed on
// Nov 15 for a user in Asia/Bangkok (UTC+7, 10:00 local).

import { addDays, differenceInCalendarDays, isAfter, startOfDay } from "date-fns";

/**
 * Return the list of local Date objects (at 00:00 local) that an activity covers.
 * An event ending exactly at midnight is treated as ending the previous day
 * (standard iCal / Google Cal convention).
 */
export function activityDayRange(activity) {
    const start = new Date(activity.start_time);
    const endRaw = new Date(activity.end_time);
    const startDay = startOfDay(start);
    let endDay = startOfDay(endRaw);
    if (endRaw.getTime() === endDay.getTime() && isAfter(endDay, startDay)) {
        // Ends exactly at midnight → prior day is the last day covered.
        endDay = addDays(endDay, -1);
    }
    if (isAfter(startDay, endDay)) endDay = startDay; // 1-day minimum (defensive)
    const out = [];
    for (let d = startDay; !isAfter(d, endDay); d = addDays(d, 1)) out.push(d);
    return out;
}

export function isMultiDay(activity) {
    return activityDayRange(activity).length > 1;
}

/**
 * Keep activities that overlap the half-open local window
 * [windowStart..windowEnd] (both at local 00:00, inclusive).
 */
export function activitiesInWindow(activities, windowStart, windowEnd) {
    return activities.filter(a => {
        const days = activityDayRange(a);
        const first = days[0];
        const last = days[days.length - 1];
        return !(isAfter(windowStart, last) || isAfter(first, windowEnd));
    });
}

/**
 * Build a lane-packed layout for one week starting at `weekStart` (local 00:00).
 * Returns `[{ activity, startCol: 0..6, span: 1..7, lane: 0..N, continuesLeft, continuesRight }]`.
 */
export function laneLayoutForWeek(activities, weekStart) {
    const weekEnd = addDays(weekStart, 6);
    const raw = [];
    for (const a of activities) {
        const days = activityDayRange(a);
        const first = days[0];
        const last = days[days.length - 1];
        if (isAfter(weekStart, last) || isAfter(first, weekEnd)) continue;
        const clipStart = isAfter(weekStart, first) ? weekStart : first;
        const clipEnd = isAfter(last, weekEnd) ? weekEnd : last;
        const startCol = differenceInCalendarDays(clipStart, weekStart);
        const span = differenceInCalendarDays(clipEnd, clipStart) + 1;
        raw.push({
            activity: a,
            startCol,
            span,
            continuesLeft: isAfter(weekStart, first),
            continuesRight: isAfter(last, weekEnd),
            _absStart: first.getTime(),
            _totalDays: days.length,
        });
    }
    // Longer-spanning / earlier events get the top lanes so they stay stable
    // across weeks when they cross a week boundary.
    raw.sort((a, b) => {
        if (b._totalDays !== a._totalDays) return b._totalDays - a._totalDays;
        if (a._absStart !== b._absStart) return a._absStart - b._absStart;
        return (a.activity.id || "").localeCompare(b.activity.id || "");
    });
    const lanes = []; // lanes[i] -> list of [startCol, endCol] used
    for (const it of raw) {
        const endCol = it.startCol + it.span - 1;
        let laneIdx = -1;
        for (let i = 0; i < lanes.length; i++) {
            const conflict = lanes[i].some(([s, e]) => !(endCol < s || it.startCol > e));
            if (!conflict) { laneIdx = i; break; }
        }
        if (laneIdx === -1) {
            lanes.push([[it.startCol, endCol]]);
            it.lane = lanes.length - 1;
        } else {
            lanes[laneIdx].push([it.startCol, endCol]);
            it.lane = laneIdx;
        }
        delete it._absStart;
        delete it._totalDays;
    }
    return raw;
}
